/* eslint-disable max-lines */
import { createApi } from '@reduxjs/toolkit/query/react';
import { ClaimStatusIndicator } from '@strata/react-status-indicators';

import { TREATMENT_API_URL } from 'api/APIBase';
import { handleError, handleRTKQueryError } from 'services/Error';
import { mapTreatmentDetails } from 'services/treatmentHelpers';
import { getBaseQuery } from 'store/api/apiBaseQuery';
import { mapClaim } from 'store/api/claimApi';

const { Statuses } = ClaimStatusIndicator;

const TagType = {
    TREATMENT: 'Treatment',
    TREATMENT_MERGE_CASE: 'TreatmentMergeCase',
    TREATMENT_MERGE_CASES: 'TreatmentMergeCases',
};

const invalidateResolvedMergeCase = (result, error, { caseId }) => [
    TagType.TREATMENT_MERGE_CASES,
    { type: TagType.TREATMENT_MERGE_CASE, id: caseId },
    ...(result?.treatmentIds ?? []).map((id) => ({ type: TagType.TREATMENT, id })),
];

const mapTreatmentMergeCase = (mergeCase) => ({
    id: mergeCase.id,
    fmhPatientId: mergeCase.fmh_patient_id,
    mergedFmhPatientId: mergeCase.merged_fmh_patient_id,
    productServiceOid: mergeCase.product_service_oid,
    treatmentIds: mergeCase.treatment_ids,
    status: mergeCase.status,
    survivingTreatmentId: mergeCase.surviving_treatment_id,
    resolvedBy: mergeCase.resolved_by,
    resolvedAt: mergeCase.resolved_at,
    createdAt: mergeCase.created_at,
});

const mapRejectedClaimReferral = (referral) =>
    referral
        ? {
              referralId: referral.referral_id,
              friendlyId: referral.friendly_id,
              referralStatus: referral.referral_status,
              segmentId: referral.segment_id,
              assignedParentPharmacyNpi: referral.assigned_parent_pharmacy_npi,
          }
        : null;

export const mapRejectedClaim = (rejectedClaim) => {
    const referral = mapRejectedClaimReferral(rejectedClaim.referral);

    return {
        ...mapClaim({
            ...rejectedClaim,
            prescription_number: rejectedClaim.drug_prescription_number,
            assigned_parent_pharmacy_npi: referral?.assignedParentPharmacyNpi,
            claim_status: Statuses.REJECTED,
        }),
        treatmentRejectedClaimId: rejectedClaim.treatment_rejected_claim_id,
        referralId: rejectedClaim.referral_id,
        referral,
        drugFillNumber: rejectedClaim.drug_fill_number,
        historyClaimId: rejectedClaim.history_claim_id,
        sequenceNumber: rejectedClaim.sequence_number,
        pharmacyNpi: rejectedClaim.supplier_npi,
        linkedAt: rejectedClaim.linked_at,
    };
};

const toRouteCheckBody = (payerId, routeCheck) => ({
    payer_id: payerId,
    segment_id: routeCheck.segmentId,
    member_id: routeCheck.memberId,
    patient_first_name: routeCheck.patientFirstName,
    patient_dob: routeCheck.patientDob,
    product_service_oid: routeCheck.productServiceOid,
    is_brand: routeCheck.isBrand,
    benefit: routeCheck.benefit,
    member_benefit_group: routeCheck.memberBenefitGroup,
    patient_zip: routeCheck.patientZip,
    supplier_npi: routeCheck.supplierNpi,
    offer_rate: null,
    prescriber_npi: routeCheck.prescriberNpi,
    prescriber_practice_npi: null,
    marketplace_participants: routeCheck.marketplaceParticipants,
    place_of_service: routeCheck.placeOfService,
    therapeutic_category: routeCheck.therapeuticCategory,
    check_only: true,
    servicing_supplier: null,
    preferred_pharmacy_offer: routeCheck.preferredPharmacyOffer
        ? {
              parent_pharmacy_npi: routeCheck.preferredPharmacyOffer.parentPharmacyNpi,
              route_code: routeCheck.preferredPharmacyOffer.routeCode,
              allow_route_override: routeCheck.preferredPharmacyOffer.allowRouteOverride,
              offer_rate: null,
          }
        : null,
});

export const treatmentApi = createApi({
    baseQuery: getBaseQuery(TREATMENT_API_URL),
    reducerPath: 'treatmentApi',
    tagTypes: Object.values(TagType),
    endpoints: (build) => ({
        getTreatmentMergeCases: build.query({
            query: ({ payerId }) => `admin/${payerId}/treatment-merge-cases`,
            transformResponse: (response) => response.map(mapTreatmentMergeCase),
            providesTags: [TagType.TREATMENT_MERGE_CASES],
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving treatment merge cases.',
            ),
        }),
        getTreatmentMergeCase: build.query({
            query: ({ payerId, caseId }) => `admin/${payerId}/treatment-merge-cases/${caseId}`,
            transformResponse: mapTreatmentMergeCase,
            providesTags: (result, error, { caseId }) => [
                { type: TagType.TREATMENT_MERGE_CASE, id: caseId },
            ],
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving the treatment merge case.',
            ),
        }),
        getTreatmentsDetails: build.query({
            queryFn: async ({ payerId, treatmentIds }, api, extraOptions, baseQuery) => {
                const responses = await Promise.all(
                    treatmentIds.map((id) => baseQuery(`admin/${payerId}/treatments/${id}`)),
                );
                const failed = responses.find((response) => response.error);
                if (failed) {
                    handleError('An error occurred retrieving treatment details.', failed.error);
                    return { error: failed.error };
                }
                return { data: responses.map((response) => mapTreatmentDetails(response.data)) };
            },
            providesTags: (result, error, { treatmentIds }) =>
                treatmentIds.map((id) => ({ type: TagType.TREATMENT, id })),
        }),
        mergeTreatmentMergeCase: build.mutation({
            query: ({ payerId, caseId, survivingTreatmentId }) => ({
                url: `admin/${payerId}/treatment-merge-cases/${caseId}/merge`,
                method: 'POST',
                body: { surviving_treatment_id: survivingTreatmentId },
            }),
            transformResponse: mapTreatmentMergeCase,
            invalidatesTags: invalidateResolvedMergeCase,
        }),
        dismissTreatmentMergeCase: build.mutation({
            query: ({ payerId, caseId }) => ({
                url: `admin/${payerId}/treatment-merge-cases/${caseId}/dismiss`,
                method: 'POST',
            }),
            transformResponse: mapTreatmentMergeCase,
            invalidatesTags: invalidateResolvedMergeCase,
        }),
        getTreatmentDetails: build.query({
            query: ({ payerId, treatmentId }) => `admin/${payerId}/treatments/${treatmentId}`,
            transformResponse: mapTreatmentDetails,
            providesTags: (result, error, { treatmentId }) => [
                { type: TagType.TREATMENT, id: treatmentId },
            ],
            transformErrorResponse: (err) => {
                if (err.status === 404) {
                    handleError('Treatment not found.', err);
                } else {
                    handleError('An error occurred retrieving treatment details.', err);
                }
            },
        }),
        convertBiosimilar: build.mutation({
            query: ({ payerId, treatmentId, body }) => ({
                url: `admin/${payerId}/treatment_cohort/${treatmentId}/transition/product`,
                method: 'POST',
                body,
            }),
        }),
        getTherapyGroupContainmentConfig: build.query({
            query: ({ payerId, segmentId }) =>
                `admin/${payerId}/treatment_cohort/${segmentId}/therapy-group-containment-config`,
            transformResponse: (response) => ({
                segmentTherapyGroupContainmentOn: response?.referral_creation_policy_on,
            }),
            transformErrorResponse: (err) => {
                if (err.status === 404) {
                    handleError('Therapy group containment configuration not found.', err);
                } else {
                    handleError(
                        'An error occurred retrieving therapy group containment configuration.',
                        err,
                    );
                }
            },
        }),
        getCohortPeerTreatments: build.query({
            query: ({ payerId, treatmentId }) =>
                `admin/${payerId}/treatment_cohort/${treatmentId}/cohort-peers`,
            transformResponse: (response) => ({
                therapyGroupName: response.therapy_group_name,
                treatments: response.treatments.map((treatment) => ({
                    treatmentId: treatment.treatment_id,
                    productServiceOid: treatment.product_service_oid,
                    isBrand: treatment.is_brand,
                    treatmentStatus: treatment.treatment_status,
                })),
            }),
            transformErrorResponse: (err) => {
                if (err.status !== 404) {
                    handleError('An error occurred retrieving peer treatments for cohort.', err);
                }
            },
        }),
        getRejectedClaimsForTreatment: build.query({
            query: ({ payerId, treatmentId }) =>
                `admin/${payerId}/treatments/${treatmentId}/rejected-claims`,
            transformResponse: (response) => response.rejected_claims.map(mapRejectedClaim),
            transformErrorResponse: (err) => {
                if (err.status !== 404) {
                    handleError('An error occurred retrieving rejected claims.', err);
                }
            },
        }),
        getTestClaimsForTreatment: build.query({
            query: ({ payerId, treatmentId, segmentId }) =>
                `admin/${payerId}/treatments/${treatmentId}/test-claims?segment_id=${segmentId}`,
            transformResponse: (response) =>
                response.test_claims.map((testClaim) => ({
                    testClaimRequestId: testClaim.test_claim_request_id,
                    testClaimResponseId: testClaim.test_claim_response_id,
                    createdAt: testClaim.created_at,
                    dateOfService: testClaim.date_of_service,
                    ndc: testClaim.ndc,
                    pharmacyNpi: testClaim.pharmacy_npi,
                    prescriberNpi: testClaim.prescriber_npi,
                    quantity: testClaim.quantity,
                    daysSupply: testClaim.days_supply,
                    dawCode: testClaim.dawcode,
                    memberId: testClaim.member_id,
                    disposition: testClaim.disposition,
                    cleanStatus: testClaim.clean_status,
                    rejectCode: testClaim.reject_code,
                    outcome: testClaim.outcome,
                    rejectCodes: testClaim.reject_codes,
                    rejectMessages: testClaim.reject_messages,
                })),
            transformErrorResponse: (err) => {
                if (err.status !== 404) {
                    handleError('An error occurred retrieving test claims.', err);
                }
            },
        }),
        getReferralCreationDecisionsForTreatment: build.query({
            query: ({ payerId, treatmentId }) =>
                `admin/${payerId}/treatments/${treatmentId}/referral-creation-decisions`,
            transformResponse: (response) =>
                response.decisions.map((decision) => ({
                    decisionId: decision.decision_id,
                    decisionTime: decision.decision_time,
                    outcome: decision.outcome,
                    opportunitySource: decision.opportunity_source,
                    sourceEvidenceId: decision.source_evidence_id,
                    createdReferralId: decision.created_referral_id,
                    createdReferralFriendlyId: decision.created_referral_friendly_id,
                    billingParentPharmacyNpi: decision.billing_parent_pharmacy_npi,
                    reasons: decision.reasons,
                })),
            transformErrorResponse: (err) => {
                if (err.status !== 404) {
                    handleError('An error occurred retrieving referral creation decisions.', err);
                }
            },
        }),
        getReferralTransitionInfo: build.query({
            query: ({ payerId, referralId }) =>
                `admin/${payerId}/referrals/${referralId}/transition`,
            transformResponse: (response) =>
                response.map((transition) => ({
                    transitionId: transition.transition_id,
                    treatmentCohortId: transition.treatment_cohort_id,
                    priorTreatmentId: transition.prior_treatment_id,
                    priorReferralId: transition.prior_referral_id,
                    priorProductServiceOid: transition.prior_product_service_oid,
                    priorIsBrand: transition.prior_is_brand,
                    newTreatmentId: transition.new_treatment_id,
                    newReferralId: transition.new_referral_id,
                    requestedProductServiceOid: transition.requested_product_service_oid,
                    requestedIsBrand: transition.requested_is_brand,
                    matchedReferralRole: transition.matched_referral_role,
                    isPending: transition.is_pending,
                    transitionStart: transition.transition_start,
                    reason: transition.reason,
                })),
            transformErrorResponse: (err) => {
                if (err.status !== 404) {
                    handleError('An error occurred retrieving referral transition info.', err);
                }
            },
        }),
        getIsTestClaimEnabled: build.query({
            query: ({ payerId, segmentId }) =>
                `admin/${payerId}/available-integrations/is-test-claim-enabled/${segmentId}`,
        }),
        runTestClaimsForProduct: build.mutation({
            query: ({
                payerId,
                segmentId,
                memberId,
                patientFirstName,
                patientLastName,
                patientDob,
                prescriberNpi,
                productServiceIds,
                asOfDate,
                parentSupplierNpi,
            }) => ({
                url: `admin/${payerId}/test-claim/all-ndcs`,
                method: 'POST',
                params: {
                    segment_id: segmentId,
                },
                body: {
                    payer_id: payerId,
                    member_id: memberId,
                    patient_first_name: patientFirstName,
                    patient_last_name: patientLastName,
                    patient_dob: patientDob,
                    prescriber_npi: prescriberNpi,
                    product_service_ids: productServiceIds,
                    as_of_date: asOfDate,
                    // Treatment resolves this parent NPI to the child supplier the claim bills
                    // under, and falls back to its own claim check NPI when we omit it.
                    parent_supplier_npi: parentSupplierNpi ?? null,
                },
            }),
        }),
        runConversionCleanlinessPreview: build.mutation({
            query: ({
                payerId,
                segmentId,
                memberId,
                patientFirstName,
                patientLastName,
                patientDob,
                prescriberNpi,
                productServiceIds,
                productServiceOid,
                asOfDate,
                parentSupplierNpi,
                treatmentId,
                referralId,
                lineOfBusiness,
                routeCheck,
            }) => ({
                url: `admin/${payerId}/treatment_cohort/conversion-cleanliness`,
                method: 'POST',
                params: {
                    segment_id: segmentId,
                },
                body: {
                    payer_id: payerId,
                    member_id: memberId,
                    patient_first_name: patientFirstName,
                    patient_last_name: patientLastName,
                    patient_dob: patientDob,
                    prescriber_npi: prescriberNpi,
                    product_service_ids: productServiceIds,
                    product_service_oid: productServiceOid,
                    as_of_date: asOfDate,
                    treatment_id: treatmentId,
                    // Needed to persist the preview rate for the admin convert path.
                    ...(referralId && { referral_id: referralId }),
                    // Treatment resolves this parent NPI to the child supplier the claim bills
                    // under, and falls back to its own claim check NPI when we omit it.
                    parent_supplier_npi: parentSupplierNpi ?? null,
                    line_of_business: lineOfBusiness ?? null,
                    // Omitted when the destination pharmacy is chosen by routing.
                    ...(routeCheck && { route_check: toRouteCheckBody(payerId, routeCheck) }),
                },
            }),
        }),
    }),
});

export const {
    useGetTreatmentDetailsQuery,
    useGetTherapyGroupContainmentConfigQuery,
    useLazyGetTreatmentDetailsQuery,
    useConvertBiosimilarMutation,
    useGetCohortPeerTreatmentsQuery,
    useGetReferralCreationDecisionsForTreatmentQuery,
    useGetReferralTransitionInfoQuery,
    useGetRejectedClaimsForTreatmentQuery,
    useGetTestClaimsForTreatmentQuery,
    useGetTreatmentMergeCasesQuery,
    useGetTreatmentMergeCaseQuery,
    useGetTreatmentsDetailsQuery,
    useMergeTreatmentMergeCaseMutation,
    useDismissTreatmentMergeCaseMutation,
    useGetIsTestClaimEnabledQuery,
    useRunTestClaimsForProductMutation,
    useRunConversionCleanlinessPreviewMutation,
} = treatmentApi;
